import React, { useMemo } from "react";
import { MotiView, AnimatePresence } from "moti";
import { StyleSheet } from "react-native";
import type { EditingTeamRole } from "@axio/graphql/generated/graphql";

import AssignmentPanel from "./AssignmentPanel";
import ExternalLinkPanel from "./ExternalLinkPanel";
import ModuleDescriptionPanel from "./ModuleDescriptionPanel";
import ManualActivityPanel from "./ManualActivityPanel";
import RecommendationPanel from "./RecommendationPanel";
import SuggestedContentPanel from "./SuggestedContentPanel";
import OverviewPanel from "./OverviewPanel";
import type { TeamManagementUserOption } from "./TeamManagementPanel";
import type {
  CourseModernizerReviewTeamManagement,
  CourseEvaluatorReviewData,
  CourseEvaluatorReviewSidebarItem,
  PublishManualStep,
  StructuredFieldEdits,
} from "../types/reviewTypes";
import type { ActivityDetail } from "../types/reviewTypes";

/**
 * Extract the entity node ID from a composite manual step ID.
 * Manual step IDs follow the pattern `<publishEventId>:<entityNodeId>`
 * or `<publishEventId>:<entityNodeId>:agent:<agentStepId>`.
 */
function extractEntityId(manualStepId: string): string | null {
  const segments = manualStepId.split(":");
  return segments.length >= 2 ? segments[1] : null;
}

function isPageLikeItem(selectedReviewItem: CourseEvaluatorReviewSidebarItem) {
  const itemTypeMeta = selectedReviewItem.meta?.find((meta) => meta.kind === "itemType");
  const itemType = itemTypeMeta?.kind === "itemType" ? itemTypeMeta.itemType : undefined;
  return itemType === "page" || itemType === "webpage";
}

interface ReviewContentPanelProps {
  review: CourseEvaluatorReviewData;
  selectedReviewItem: CourseEvaluatorReviewSidebarItem | null;
  activityDetails: Record<string, ActivityDetail>;
  isArchitect: boolean;
  currentUserId: string | null;
  teamManagement: CourseModernizerReviewTeamManagement | null;
  teamUserOptions: TeamManagementUserOption[];
  teamSearchQuery: string;
  onTeamSearchQueryChange: (value: string) => void;
  selectedTeamRole: EditingTeamRole;
  onSelectedTeamRoleChange: (role: EditingTeamRole) => void;
  isSavingTeamManagement: boolean;
  onEditingTeamChange: (nextMembers: Array<{ userId: string; role: EditingTeamRole }>) => void;
  publishManualSteps?: PublishManualStep[];
  isExpanded?: boolean;
  isEditDisabled?: boolean;
  onStartReviewing: () => void;
  onOverlayVisibilityChange?: (isVisible: boolean) => void;
  onSaveEdit?: (item: CourseEvaluatorReviewSidebarItem, editedFields: StructuredFieldEdits) => void;
  onCreateIle?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onPreviewIle?: (item: CourseEvaluatorReviewSidebarItem) => void;
  isGeneratingIle?: boolean;
  onAccept?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onReject?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onAcceptWithoutNav?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onRejectWithoutNav?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onJumpToEntity?: (entityId: string, dimension: string) => void;
}

function ReviewContentPanel({
  review,
  selectedReviewItem,
  activityDetails,
  isArchitect,
  currentUserId,
  teamManagement,
  teamUserOptions,
  teamSearchQuery,
  onTeamSearchQueryChange,
  selectedTeamRole,
  onSelectedTeamRoleChange,
  isSavingTeamManagement,
  onEditingTeamChange,
  publishManualSteps,
  isExpanded,
  isEditDisabled,
  onStartReviewing,
  onOverlayVisibilityChange,
  onSaveEdit,
  onCreateIle,
  onPreviewIle,
  isGeneratingIle = false,
  onAccept,
  onReject,
  onAcceptWithoutNav,
  onRejectWithoutNav,
  onJumpToEntity,
}: ReviewContentPanelProps) {
  const itemKey = selectedReviewItem?.id ?? "overview";
  const isReadOnly = Boolean(isEditDisabled);

  return (
    <AnimatePresence exitBeforeEnter>
      <MotiView
        key={itemKey}
        style={isExpanded && selectedReviewItem ? styles.expandedRoot : undefined}
        from={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ type: "timing", duration: 150 }}>
        {selectedReviewItem === null ? (
          <OverviewPanel
            review={review}
            isArchitect={isArchitect}
            currentUserId={currentUserId}
            teamManagement={teamManagement}
            teamUserOptions={teamUserOptions}
            teamSearchQuery={teamSearchQuery}
            onTeamSearchQueryChange={onTeamSearchQueryChange}
            selectedTeamRole={selectedTeamRole}
            onSelectedTeamRoleChange={onSelectedTeamRoleChange}
            isSavingTeamManagement={isSavingTeamManagement}
            onEditingTeamChange={onEditingTeamChange}
            onStartReviewing={onStartReviewing}
            onOverlayVisibilityChange={onOverlayVisibilityChange}
            onAccept={onAccept}
            onReject={onReject}
            onAcceptWithoutNav={onAcceptWithoutNav}
            onRejectWithoutNav={onRejectWithoutNav}
            onJumpToEntity={onJumpToEntity}
            canEdit={!isReadOnly}
          />
        ) : (
          <PanelContent
            selectedReviewItem={selectedReviewItem}
            activityDetails={activityDetails}
            publishManualSteps={publishManualSteps}
            isExpanded={isExpanded}
            isEditDisabled={isReadOnly}
            onSaveEdit={onSaveEdit}
            onCreateIle={onCreateIle}
            onPreviewIle={onPreviewIle}
            isGeneratingIle={isGeneratingIle}
            sourceLabel={review.sourceLabel}
            isUploadedCourse={!review.canPublishToLms}
          />
        )}
      </MotiView>
    </AnimatePresence>
  );
}

/** Inner component to keep the switch logic clean */
export function PanelContent({
  selectedReviewItem,
  activityDetails,
  publishManualSteps,
  isExpanded,
  isEditDisabled,
  onSaveEdit,
  onCreateIle,
  onPreviewIle,
  isGeneratingIle,
  sourceLabel,
  isUploadedCourse,
}: {
  selectedReviewItem: CourseEvaluatorReviewSidebarItem;
  activityDetails: Record<string, ActivityDetail>;
  publishManualSteps?: PublishManualStep[];
  isExpanded?: boolean;
  isEditDisabled?: boolean;
  onSaveEdit?: (item: CourseEvaluatorReviewSidebarItem, editedFields: StructuredFieldEdits) => void;
  onCreateIle?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onPreviewIle?: (item: CourseEvaluatorReviewSidebarItem) => void;
  isGeneratingIle: boolean;
  sourceLabel: string;
  isUploadedCourse?: boolean;
}) {
  const activityDetail = activityDetails[selectedReviewItem.id] ?? null;
  const handleSaveEdit = onSaveEdit
    ? (fields: StructuredFieldEdits) => onSaveEdit(selectedReviewItem, fields)
    : undefined;
  const handleCreateIle = onCreateIle ? () => onCreateIle(selectedReviewItem) : undefined;
  const handlePreviewIle = onPreviewIle ? () => onPreviewIle(selectedReviewItem) : undefined;
  const isManuallyAuthoredItem =
    activityDetail?.isManuallyAuthored === true || selectedReviewItem.meta?.some((meta) => meta.kind === "manual");

  const entityManualSteps = useMemo(() => {
    if (!publishManualSteps || publishManualSteps.length === 0) return [];
    return publishManualSteps.filter((step) => extractEntityId(step.id) === selectedReviewItem.id);
  }, [publishManualSteps, selectedReviewItem.id]);

  if (isManuallyAuthoredItem) {
    return (
      <ManualActivityPanel
        selectedReviewItem={selectedReviewItem}
        activityDetail={activityDetail}
        isExpanded={isExpanded}
      />
    );
  }

  switch (selectedReviewItem.kind) {
    case "moduleDescription":
      return (
        <ModuleDescriptionPanel
          selectedReviewItem={selectedReviewItem}
          activityDetail={activityDetail}
          manualSteps={entityManualSteps}
          isExpanded={isExpanded}
          isEditDisabled={isEditDisabled}
          onSaveEdit={handleSaveEdit}
          sourceLabel={sourceLabel}
        />
      );
    case "recommendation":
      return (
        <RecommendationPanel
          selectedReviewItem={selectedReviewItem}
          activityDetail={activityDetail}
          manualSteps={entityManualSteps}
          isExpanded={isExpanded}
          isEditDisabled={isEditDisabled}
          onSaveEdit={handleSaveEdit}
        />
      );
    case "assignment": {
      const itemTypeMeta = selectedReviewItem.meta?.find((m) => m.kind === "itemType");
      const itemType = itemTypeMeta?.kind === "itemType" ? itemTypeMeta.itemType : undefined;
      const isExternalLink =
        itemType === "externalLink" ||
        itemType === "link" ||
        activityDetail?.activityType === "externalLink" ||
        activityDetail?.activityType === "link";
      if (isExternalLink) {
        return (
          <ExternalLinkPanel
            selectedReviewItem={selectedReviewItem}
            activityDetail={activityDetail}
            manualSteps={entityManualSteps}
            isExpanded={isExpanded}
            isEditDisabled={isEditDisabled}
            onSaveEdit={handleSaveEdit}
          />
        );
      }
      return (
        <AssignmentPanel
          selectedReviewItem={selectedReviewItem}
          activityDetail={activityDetail}
          manualSteps={entityManualSteps}
          isExpanded={isExpanded}
          isEditDisabled={isEditDisabled}
          onSaveEdit={handleSaveEdit}
          isUploadedCourse={isUploadedCourse}
        />
      );
    }
    case "suggestedContent":
      if (isPageLikeItem(selectedReviewItem)) {
        return (
          <AssignmentPanel
            selectedReviewItem={selectedReviewItem}
            activityDetail={activityDetail}
            manualSteps={entityManualSteps}
            isExpanded={isExpanded}
            isEditDisabled={isEditDisabled}
            onSaveEdit={handleSaveEdit}
            isUploadedCourse={isUploadedCourse}
          />
        );
      }

      return (
        <SuggestedContentPanel
          selectedReviewItem={selectedReviewItem}
          activityDetail={activityDetail}
          manualSteps={entityManualSteps}
          isExpanded={isExpanded}
          isEditDisabled={isEditDisabled}
          onSaveEdit={handleSaveEdit}
          onCreateIle={handleCreateIle}
          onPreviewIle={handlePreviewIle}
          isGeneratingIle={isGeneratingIle}
        />
      );
    default:
      return null;
  }
}

const styles = StyleSheet.create({
  expandedRoot: {
    flex: 1,
    minHeight: 0,
  },
});

export default ReviewContentPanel;
